import React from 'react';
import { Paper, Table, TableRow, TableCell, TableHead, TableBody, makeStyles, CircularProgress, Box } from '@material-ui/core';
import clsx from 'clsx';
import { GatewayDefinition } from '@moderepo/mode-apis';
import { BaseListCompFieldsSet, BaseListCompField, BaseListCompFieldsSettings, createHeaderColumn, BaseListCompProps, useModeTableStyle,
    useModePanelStyle, BaseListCompDataItem, createRemoveItemCell, createPreviewItemCell, CompPanelHeader } from '@moderepo/mode-app-components';



const useStyle = makeStyles(() => {
    return {

        panelHeaderIcon: {
        },

        panelContent: {
            padding      : 0,
            height       : '100%',
            display      : 'flex',
            flexDirection: 'column',
        },

        tableContainer: {
            overflow: 'auto',
            flex    : 1,
        },

        tableCol: {
            '&.deviceClass-col': {
                textAlign: 'left',
            },
            '&.name-col': {
                textAlign: 'left',
                width    : '100%',
            },
            '&.createdAt-col': {
                textAlign: 'left',
            },
        },
    };
});


export interface RCDisplayableGatewayDefinitionsTableDataItem {
    readonly name?: string;
    readonly deviceClass?: string;
    readonly createdAt?: string;
}


export interface RCGatewayDefinitionsTableDataItem extends BaseListCompDataItem<GatewayDefinition> {
    readonly displayValue: RCDisplayableGatewayDefinitionsTableDataItem;
}


/**
 * Table columns setting for Alerts table. Alerts table columns settings MUST contain these column keys
 */
export interface RCGatewayDefinitionsTableFieldsSet extends BaseListCompFieldsSet {
    readonly name?: BaseListCompField;
    readonly deviceClass?: BaseListCompField;
    readonly createdAt?: BaseListCompField;
}



export interface RCGatewayDefinitionsTableProps extends BaseListCompProps<GatewayDefinition> {
    readonly listData: readonly RCGatewayDefinitionsTableDataItem[] | undefined;
    readonly fieldsSettings: BaseListCompFieldsSettings<RCGatewayDefinitionsTableFieldsSet>;
    readonly onListItemSelected: (gatewayDef: GatewayDefinition)=> void;
}


export const RCGatewayDefinitionsTable: React.FC<RCGatewayDefinitionsTableProps> = (props: RCGatewayDefinitionsTableProps) => {

    const tableClasses = useModeTableStyle();
    const panelClasses = useModePanelStyle();
    const classes = useStyle();


    return (
        <Paper elevation={2} className={clsx(panelClasses.root, props.className)}>
            <CompPanelHeader {...props} />

            <div className={clsx(panelClasses.panelContent, classes.panelContent)}>

                {props.listData && props.listData.length > 0 && (
                    <>
                        <div className={classes.tableContainer}>
                            <Table className={tableClasses.root} size="medium">
                                <TableHead className={tableClasses.tableHeader}>
                                    <TableRow className={tableClasses.tableHeaderRow}>
                                        {/* filter out hidden columns first and then go through each column to create Header Cell */}
                                        {Object.keys(props.fieldsSettings.fields).filter((fieldName: string) => {
                                            return !props.fieldsSettings.fields[fieldName]?.hidden;
                                        }).map((fieldName: string) => {
                                            const field = props.fieldsSettings.fields[fieldName];
                                            if (field && !field.hidden) {
                                                return createHeaderColumn(
                                                    props.fieldsSettings,
                                                    fieldName,
                                                    field, `${tableClasses.tableCol} ${classes.tableCol} ${fieldName}-col`,
                                                );
                                            }
                                            return <></>;
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {props.listData.map((dataItem: RCGatewayDefinitionsTableDataItem): JSX.Element => {
                                        return (
                                            <TableRow
                                                className={clsx(tableClasses.tableBodyRow, 'clickable', dataItem.selected && 'selected')}
                                                key={dataItem.actualValue.deviceClass}
                                                onClick={() => {
                                                    if (props.onListItemSelected) {
                                                        props.onListItemSelected(dataItem.actualValue);
                                                    }
                                                }}
                                            >
                                                {/* filter out hidden columns first and then go through each column to create Table Cell */}
                                                {Object.keys(props.fieldsSettings.fields).filter((fieldName: string) => {
                                                    return !props.fieldsSettings.fields[fieldName]?.hidden;
                                                }).map((fieldName: string) => {

                                                    if (fieldName === 'remove' && props.fieldsSettings.fields.remove
                                                        && !props.fieldsSettings.fields.remove.hidden) {
                                                        return (
                                                            createRemoveItemCell(dataItem, clsx(
                                                                tableClasses.tableCol, classes.tableCol, `${fieldName}-col`,
                                                            ),
                                                            props.onRemoveItemClicked)
                                                        );
                                                    }

                                                    if (fieldName === 'preview' && props.fieldsSettings.fields.preview
                                                        && !props.fieldsSettings.fields.preview.hidden) {
                                                        return createPreviewItemCell(
                                                            dataItem, clsx(tableClasses.tableCol, classes.tableCol, `${fieldName}-col`),
                                                            props.onPreviewItemClicked,
                                                        );
                                                    }

                                                    /**
                                                     * Handle all the other fields that don't have special UI. These are the fields that
                                                     * display dataItem.displayValue as plain text
                                                     */
                                                    if (props.fieldsSettings.fields[fieldName]
                                                        && !(props.fieldsSettings.fields[fieldName] as BaseListCompField).hidden) {
                                                        return (
                                                            <TableCell
                                                                key={fieldName}
                                                                className={clsx(tableClasses.tableCol, classes.tableCol, `${fieldName}-col`)}
                                                            >
                                                                {dataItem.displayValue[fieldName]}
                                                            </TableCell>
                                                        );
                                                    }
                                                    return <></>;
                                                })}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>

                        {props.paginationComp}
                    </>
                )}

                {props.listData && props.listData.length === 0 && props.emptyDataMessageComp}

                {!props.listData && (
                    <Box padding={2}>
                        <CircularProgress />
                    </Box>
                )}
            </div>
        </Paper>
    );
};
